import { Typography } from "@mui/material";
import { InputFieldInputStyle } from "commons/styled.components";
import Button from "components/button";
import Label from "components/label";
import Link from "components/link";
import PasswordValidator from "components/passwordvalidation";
import PasswordTextField from "components/textfield/PasswordTextField";
import withLoader from "hoc/loader";
import keys from "locale/keys";
import { useState } from "react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { COLORS } from "utils/colors";
import { ROUTES } from "utils/constants";

import { SignupValidationModel } from "../interface";
import {
  InputFieldWrapper,
  LabelWithLinkWrapper,
  LoginTextField,
  LoginWrapper,
  SignupNameWrapper,
  TermsLink,
} from "../styled.components";
import {
  validateEmail,
  validatePasswordForStrength,
  validateSignup,
  validateSimpleInput,
} from "../utils";
import BrandsBanner from "./BrandsBanner";
import HowItWorks from "./HowItWorks";
import { SignupProps } from "./interface";
import { SignupBaseForm, SignupContentWrapper } from "./styled.components";

const Signup = (props: SignupProps) => {
  const {
    isSignupSuccess,
    onLoginClickedHandler,
    onSignupHandler,
    isAccountAlreadyExists,
    details,
  } = props;
  const { t } = useTranslation();

  const [firstname, setFirstName] = React.useState("");
  const [lastname, setLastName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [passwordStrengthValidated, setPasswordStrengthValidated] =
    React.useState(false);
  const [validationModel, setValidationModel] = useState<SignupValidationModel>(
    {
      isValidFirstName: true,
      isValidLastName: true,
      isValidEmail: true,
      isValidPassword: true,
      isValidConfirmPassword: true,
    },
  );

  const detailsToPrefil = details?.response;
  React.useEffect(() => {
    if (detailsToPrefil) {
      setFirstName(detailsToPrefil?.firstName);
      setLastName(detailsToPrefil?.lastName);
      setEmail(detailsToPrefil?.email);
    }
  }, [detailsToPrefil]);

  const firstNameValidation = () => {
    const model = validateSimpleInput(firstname, keys.please_enter_firstname);
    setValidationModel({
      ...validationModel,
      isValidFirstName: model.isValid,
      firstNameMessage: model.message,
    });
  };

  const lastNameValidation = () => {
    const model = validateSimpleInput(lastname, keys.please_enter_lastname);
    setValidationModel({
      ...validationModel,
      isValidLastName: model.isValid,
      lastNameMessage: model.message,
    });
  };

  const emailValidation = () => {
    const model = validateEmail(email);
    setValidationModel({
      ...validationModel,
      isValidEmail: model.isValid,
      emailMessage: model.message,
    });
  };

  const passwordValidation = () => {
    const model = validatePasswordForStrength(
      password,
      passwordStrengthValidated,
    );
    setValidationModel({
      ...validationModel,
      isValidPassword: model.isValid,
      passwordMessage: model.message,
    });
  };

  const confirmPasswordValidation = () => {
    const model = validateSimpleInput(
      confirmPassword,
      keys.please_enter_confirm_password,
    );
    setValidationModel({
      ...validationModel,
      isValidConfirmPassword: model.isValid,
      confirmPasswordMessage: model.message,
    });
  };

  const onSignupButtonClicked = (event: any) => {
    event.preventDefault();
    const model = validateSignup(
      firstname,
      lastname,
      email,
      password,
      confirmPassword,
      passwordStrengthValidated,
    );
    setValidationModel(model);

    if (
      model.isValidFirstName &&
      model.isValidLastName &&
      model.isValidEmail &&
      model.isValidPassword &&
      model.isValidConfirmPassword
    ) {
      onSignupHandler(firstname, lastname, email, password);
    }
  };

  if (isSignupSuccess || isAccountAlreadyExists) {
    const msg = isAccountAlreadyExists
      ? t(keys.account_exists)
      : t(keys.successfully_signup);
    return (
      <SignupContentWrapper>
        <SignupBaseForm as="div">
          <LoginWrapper>
            <Label type="large" color={COLORS.LOGIN_PRIMARY}>
              {t(keys.successful)}
            </Label>
            <Label type="medium" color={COLORS.LOGIN_SECONDARY}>
              {msg}
            </Label>
            <Button onClickHandler={onLoginClickedHandler}>
              {t(keys.login)}
            </Button>
          </LoginWrapper>
        </SignupBaseForm>
        <BrandsBanner />
        <HowItWorks />
      </SignupContentWrapper>
    );
  }

  return (
    <SignupContentWrapper>
      <SignupBaseForm onSubmit={onSignupButtonClicked}>
        <LoginWrapper>
          <Label type="large" color={COLORS.LOGIN_PRIMARY}>
            {t(keys.sign_up_title)}
          </Label>
          <Label type="medium" color={COLORS.LOGIN_SECONDARY}>
            {t(keys.welcome_enter_your_details)}
          </Label>
          <SignupNameWrapper>
            <InputFieldWrapper>
              <Label type="normalbold" color={COLORS.LOGIN_PRIMARY}>
                {t(keys.first_name)}
              </Label>
              <LoginTextField
                defaultValue={detailsToPrefil?.firstName ?? ""}
                value={firstname}
                error={!validationModel?.isValidFirstName}
                placeholder={t(keys.enter_first_name)}
                type="text"
                disabled={!!detailsToPrefil}
                helperText={validationModel?.firstNameMessage}
                onInput={(e: any) => {
                  setFirstName(e.target.value);
                }}
                onBlur={() => firstNameValidation()}
                inputProps={InputFieldInputStyle(
                  validationModel?.isValidFirstName ?? true,
                )}
              />
            </InputFieldWrapper>
            <InputFieldWrapper>
              <Label type="normalbold" color={COLORS.LOGIN_PRIMARY}>
                {t(keys.last_name)}
              </Label>
              <LoginTextField
                defaultValue={detailsToPrefil?.lastName ?? ""}
                value={lastname}
                error={!validationModel?.isValidLastName}
                placeholder={t(keys.enter_last_name)}
                type="text"
                disabled={!!detailsToPrefil}
                helperText={validationModel?.lastNameMessage}
                onInput={(e: any) => {
                  setLastName(e.target.value);
                }}
                onBlur={() => lastNameValidation()}
                inputProps={InputFieldInputStyle(
                  validationModel?.isValidLastName ?? true,
                )}
              />
            </InputFieldWrapper>
          </SignupNameWrapper>
          <InputFieldWrapper>
            <Label type="normalbold" color={COLORS.LOGIN_PRIMARY}>
              {t(keys.email)}
            </Label>
            <LoginTextField
              defaultValue={detailsToPrefil?.email ?? ""}
              value={email}
              error={!validationModel?.isValidEmail}
              placeholder={t(keys.enter_email)}
              type="email"
              disabled={!!detailsToPrefil}
              helperText={validationModel?.emailMessage}
              onInput={(e: any) => {
                setEmail(e.target.value);
              }}
              onBlur={() => emailValidation()}
              inputProps={InputFieldInputStyle(
                validationModel?.isValidEmail ?? true,
              )}
            />
          </InputFieldWrapper>
          <InputFieldWrapper>
            <Label type="normalbold" color={COLORS.LOGIN_PRIMARY}>
              {t(keys.password)}
            </Label>
            <PasswordTextField
              placeholder={t(keys.enter_password)}
              error={!validationModel?.isValidPassword}
              onInput={(e: any) => {
                setPassword(e.target.value);
              }}
              onBlur={() => passwordValidation()}
              helperText={validationModel?.passwordMessage}
              inputProps={InputFieldInputStyle(
                validationModel?.isValidPassword ?? true,
              )}
            />
          </InputFieldWrapper>
          <InputFieldWrapper>
            <Label type="normalbold" color={COLORS.LOGIN_PRIMARY}>
              {t(keys.confirm_password)}
            </Label>
            <PasswordTextField
              placeholder={t(keys.enter_password)}
              error={!validationModel?.isValidConfirmPassword}
              onInput={(e: any) => {
                setConfirmPassword(e.target.value);
              }}
              onBlur={() => confirmPasswordValidation()}
              helperText={validationModel?.confirmPasswordMessage}
              inputProps={InputFieldInputStyle(
                validationModel?.isValidConfirmPassword ?? true,
              )}
            />
          </InputFieldWrapper>
          <PasswordValidator
            password={password}
            setPasswordStrengthValidated={setPasswordStrengthValidated}
          />
          <Button
            onClickHandler={onSignupButtonClicked}
            type="submit"
            bgcolor="#df4f71"
          >
            {t(keys.start_earning_for_free)}
          </Button>
          <Typography
            variant="body2"
            color={COLORS.LOGIN_SECONDARY}
            sx={{
              fontSize: "0.8rem",
              lineHeight: 1.2,
              whiteSpace: "normal",
              textAlign: "center",
              marginTop: 1,
            }}
          >
            {t(keys.signup_terms_agreement)}
            <TermsLink
              href={`${ROUTES.POLICIES}?tab=terms`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t(keys.terms_of_service)}
            </TermsLink>{" "}
            {t(keys.signup_privacy_agreement)}
            <TermsLink
              href={`${ROUTES.POLICIES}?tab=privacy`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t(keys.privacy_policy)}
            </TermsLink>
          </Typography>
          <LabelWithLinkWrapper>
            <Label type="normalbold" color={COLORS.LOGIN_SECONDARY}>
              {t(keys.already_have_account)}
            </Label>
            <Link onClick={onLoginClickedHandler}>{t(keys.login)}</Link>
          </LabelWithLinkWrapper>
        </LoginWrapper>
      </SignupBaseForm>
      <BrandsBanner />
      <HowItWorks />
    </SignupContentWrapper>
  );
};

const PURE = withLoader(Signup);
export default PURE;
